/*
 * Copyright 2025 Intuition Robotics ltd
 */
import type {DB_Object} from "@intuitionrobotics/firebase";
import type {AuditBy} from "@intuitionrobotics/ts-common";
import type {MessageType} from "@intuitionrobotics/push-pub-sub";
import type {BaseFact} from "./fact.js";
import type {ApiWithBody, ApiWithQuery} from "@intuitionrobotics/thunderstorm";
import type {GroupType} from "@app/ir-q-app-common/types/contacts";

export enum AlertType {
	HealthReportedDataNotification = "Health reported data notification",
	PatientContactRequest          = "Customer contact request",
	ElliQInitiated                 = "ElliQ initiated",
}

export const getAlertType = (alertType: AlertType) => {
	switch (alertType) {
		case AlertType.ElliQInitiated:
			return "ElliQ initiated";
		case AlertType.PatientContactRequest:
			return "Customer contact request";
		default:
			return "Unknown";
	}
};

export type Api_UpdateAllAlerts = ApiWithBody<"/v1/alert/update-all", { patientId: string }, DB_Alert<any>[]>

export const getAlertTitle = (alert: DB_Alert<any>) => {
	if (alert.subject === "Patient contact request")
		return "Customer contact request"

	return alert.subject;
}
export const getAlertDetails = (alert: DB_Alert<any>) => alert.details;

export const Feedback_KeyFreeText = "free-text";

export enum AlertStatus {
	NEW                = "RECEIVED",
	OPEN               = "OPEN",
	IN_PROGRESS        = "IN_PROGRESS",
	REJECTED           = "USER_REJECTED",
	COMPLETE           = "COMPLETE"
}

export type AlertFeedback = {
	// Special case: If the categoryKey is equal to "other",
	// then its value is an array with a single freetext string.
	[categoryKey: string]: string[] | undefined;
}

export type MessageTrigger = {
	messageId: string
	conversationId: string
};


export type DB_Alert<K extends keyof AlertStatus> = DB_Object & {
	type: AlertType
	status: AlertStatus
	subject: string
	details: string
	_created: AuditBy
	_updated: AuditBy
	description?: string
	patientId?: string
	organizationId?: string
	category: K
	// feedback?: AlertFeedback
	// lastUpdated?: AuditBy,
	// Facts related data
	sectionName: string
	alertTrigger: string
	alertType: string
	messageTrigger?: MessageTrigger
	optOut?: "true" | "false" | "dont_contact_me" //or string?
	deploymentContext?: DeploymentContext
	deploymentContextAsString?: string
	notRelevant?: boolean
}

export type StatusChange = {
	from: AlertStatus
	to: AlertStatus
};

export type CommentMap = {
	text: string
	statusChange: StatusChange
	feedback: AlertFeedback
}

export type CommentData<K extends keyof CommentMap> = {
	type: K
	data: CommentMap[K],
	// Only to be used in order to display what status was before marking as COMPLETE and providing feedback
	previousStatus?: AlertStatus
}

export type DB_Comment<K extends keyof CommentMap> = DB_Object & CommentData<K> & {
	timestamp: AuditBy
	alertId: string
}

export type DeploymentContext = {
	rootExecutionId: string | null
	rootPlanExecutionId: string | null
	planName: string | null
	originator: string | null
	planGroups: string | null
	mainPlanName: string | null
	proactiveType: string | null
};

export type Fact_Feedback = {
	actionType: string,
	actionSelected: FeedbackAction[];
}

export type FeedbackAction = {
	actionTaken: string,
	actionDetails: string[] | null
}


export type Alert_Fact = BaseFact & {
	frequencies: string | null,
	displayDetails: string | null
	alertType: string
	messageId: string | null
	conversationId: string | null
	medicationId: string | null
	alertInitiative: string
	subject: string
	details: string
	alertState: AlertStatus
	action?: Fact_Feedback[]
	commentText?: string
	alertId: string
	alertTrigger: string
	optOut: "true" | "false" | "dont_contact_me" | null
	_auditBy: string
	alertSentTo: GroupType
}

export type FeedbackCategory = {
	key: string;
	label: string;
	options: FeedbackCategoryOption[]
}

export type FeedbackCategoryOption = {
	key: string;
	label: string;
}

export const ClinicSubjects: string[] = ["Not feeling well", "Medications", "Appointments", "Tests and treatments", "wellness"];

export const PushKey_NewAlert = "new-alert";
export type MessageType_NewAlert = MessageType<typeof PushKey_NewAlert, { organizationId: string }, DB_Alert<any>[]>

// Flattened wire shape of a DB_Alert for new-ui's Activity feed (member profile page) — that
// consumer only needs status/subject/details/timestamps, not the full Firestore DB_Object/AuditBy
// shape (nested audit-by/audit-at, category, sectionName, deploymentContext, etc.).
export type CcAlert = {
	id: string
	type: AlertType
	status: AlertStatus
	subject: string
	details: string
	createdAt: number
	createdBy: string
	updatedAt: number
	updatedBy: string
}

// Single-agent counterpart of the CRUD-mounted /v1/alert/query — alerts are Firestore-resident
// (AlertsModule), keyed by patientId, not part of the cc_patients SQL surface any other
// "*-by-agent" endpoint proxies to; this resolves unitId -> patientId server-side
// (PatientModule.queryUnique) so new-ui, like every other by-agent caller, only ever deals in
// agentId.
export type Query_AlertsByAgent = { unitId: string }
export type Api_AlertsByAgent = ApiWithQuery<"/v2/alerts/by-agent", CcAlert[], Query_AlertsByAgent>

// Status-change control on an Activity feed row (new-ui) — mirrors the legacy app-frontend
// AlertRow's status dropdown, restricted to the same three caregiver-selectable targets
// (AlertStatusClinicDropdown: OPEN/IN_PROGRESS/COMPLETE — NEW/REJECTED are never offered there
// either). `updatedBy` is supplied by the caller (the signed-in user's email), same convention as
// Body_PatientNotesCreate's createdBy — no Kaspero session lookup needed server-side.
export type Body_AlertsUpdateStatus = { unitId: string, alertId: string, status: AlertStatus, updatedBy: string }
export type Api_AlertsUpdateStatus = ApiWithBody<"/v2/alerts/update-status", Body_AlertsUpdateStatus, CcAlert>

// The Activity feed's "Mark as completed" confirmation panel (new-ui) — the single-agent
// counterpart of the legacy app-frontend StatusEntry -> Dialog_AlertFeedback flow. Completing an
// alert always carries feedback (what actions were taken as a result of the notification), unlike
// the plain OPEN/IN_PROGRESS transitions in Body_AlertsUpdateStatus above. `updatedBy` follows the
// same convention as that endpoint / Body_PatientNotesCreate's createdBy — supplied by the caller,
// no Kaspero session lookup needed server-side.
export type Body_AlertsComplete = { unitId: string, alertId: string, feedback: AlertFeedback, updatedBy: string }
export type Api_AlertsComplete = ApiWithBody<"/v2/alerts/complete", Body_AlertsComplete, CcAlert>

// Backs the top bar's notification bell badge — count of unread (status NEW/"RECEIVED") alerts
// across an entire organization, not one agent. Alerts can be scoped either by patientId
// (handleNewAlert) or directly by organizationId (handleNewOrgAlert) — see
// AlertsModule.countNewByOrg, which accounts for both.
export type Query_AlertsUnreadCountByOrg = { organizationId: string }
export type AlertsUnreadCountResponse = { count: number }
export type Api_AlertsUnreadCountByOrg = ApiWithQuery<"/v2/alerts/unread-count", AlertsUnreadCountResponse, Query_AlertsUnreadCountByOrg>
